import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useParams } from 'react-router-dom';
import { palette, radius, space, spacing, typography } from '../../../style-utils/theme';
import { down } from '../../../style-utils/media-queries';
import { useAdminResource } from './use-admin';
import { AdminTable, Tag, Tone, when, whenExact, num } from './admin-table';
import {
  Card,
  CardHead,
  CardTitle,
  CardBody,
  Tile,
  TileGrid,
  TileLabel,
  TileValue,
  TileFoot,
  Toolbar,
  Select,
  Btn,
  Empty,
  ErrorBox,
  NoteBox,
  DefList,
  Mono,
  Scroller,
  SkeletonRow,
} from './admin.styled';

/**
 * EVERY OUTSIDE SERVICE, WATCHED (ai-plans/integrations WP-10).
 *
 * The "Health & integrations" screen is the index: one card per service —
 * email, address search, the map, Stripe, Gemini, push, image storage, the PDF
 * renderer — each saying in one word whether it works, when it last worked,
 * what it last refused with (the vendor's OWN words), and today's counts. Each
 * card opens the service's page: per day, by kind, every distinct error with
 * its count, the last fifty outcomes, and the service's own panels — for email,
 * who did not get one.
 *
 * Everything comes from the outcome ledger (integration_events, migration 138)
 * through /admin/integrations. The database box, the server, its certificates
 * and the backups already have screens; they appear here as cards that link
 * there.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

/* ── words ─────────────────────────────────────────────────────────────── */

const STATUS: Record<string, { word: string; tone: Tone }> = {
  ok: { word: 'Working', tone: 'success' },
  failing: { word: 'Not working', tone: 'alert' },
  warning: { word: 'Needs a look', tone: 'warning' },
  low: { word: 'Close to its limit', tone: 'warning' },
  unconfigured: { word: 'Not set up', tone: 'neutral' },
  off: { word: 'Switched off', tone: 'neutral' },
  idle: { word: 'No calls yet', tone: 'neutral' },
  unknown: { word: 'Unknown', tone: 'warning' },
};
const statusOf = (s?: string | null) => STATUS[String(s || 'unknown')] || STATUS.unknown;

/* What each kind of call is, in words. Anything not listed is shown as sent. */
const KIND: Record<string, string> = {
  invite: 'Invitation',
  invite_resend: 'Invitation, sent again',
  verify_email: 'Confirm your email',
  verify_email_resend: 'Confirm your email, sent again',
  email_change: 'Email change link',
  email_change_notice: 'Email change notice',
  password_reset: 'Password reset',
  notification: 'Notification',
  handover: 'Shift handover to the building inbox',
  daily_report: 'Daily report',
  daily_report_prepare: 'Daily report (failed before sending)',
  custom_plan: 'Custom plan enquiry',
  waitlist: 'Waitlist signup',
  other: 'Other',
  autocomplete_address: 'Address search',
  autocomplete_city: 'City search (signup)',
  reverse: 'Address of a map pin',
  near: 'City near me (signup)',
  probe: 'Health check',
  client_style: 'Browser: map style failed',
  client_tile: 'Browser: map tiles failed',
  client_source: 'Browser: map source failed',
  client_webgl: 'Browser: map could not draw',
  client_other: 'Browser: map error',
  webhook_signature: 'Webhook refused: bad signature',
  reconcile: 'Six-hourly billing check',
  upload: 'Upload',
  delete: 'Delete',
  render: 'Render',
  fcm: 'Chrome / Android (FCM)',
  mozilla: 'Firefox',
  apple: 'Safari / iPhone',
  windows: 'Windows',
  fcm_expired: 'Chrome / Android — device gone',
  mozilla_expired: 'Firefox — device gone',
  apple_expired: 'Safari / iPhone — device gone',
  windows_expired: 'Windows — device gone',
  other_expired: 'Other — device gone',
  // Identity verification (Stays plan S2). `webhook_signature` is shared with
  // Stripe and keeps its label above.
  create: 'Verification started',
  retrieve: 'Status read',
  retrieve_sensitive: 'Result read back',
  redact: 'Redaction requested',
  extract: 'Fields stored',
  extract_failed: 'Fields could not be stored',
};
const kindLabel = (k?: string | null) => KIND[String(k || '')] || String(k || '—');

const SYSTEM_CARDS = [
  { path: 'database', label: 'Database', blurb: 'PostgreSQL on its own server.' },
  {
    path: 'server',
    label: 'Server',
    blurb: 'Memory, disk, services and the TLS certificates.',
  },
  {
    path: 'backups',
    label: 'Backups',
    blurb: 'Every database backup, on the box and in S3.',
  },
  {
    path: 'system-logs',
    label: 'System logs',
    blurb: 'Every line the box writes. Search "SendGrid" for mail.',
  },
];

/* ── layout ────────────────────────────────────────────────────────────── */

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
`;

const ServiceCard = styled.button<{ $edge: string }>`
  display: grid;
  gap: ${space(0.5)};
  align-content: start;
  min-width: 0;
  padding: ${space(2)};
  text-align: left;
  font: inherit;
  color: ${palette.ink};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-left: 4px solid ${p => p.$edge};
  border-radius: ${radius.md};
  cursor: pointer;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease;

  &:hover {
    border-color: ${palette.lineStrong};
  }
  &:focus-visible {
    outline: 2px solid ${palette.teal};
    outline-offset: 2px;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

const CardName = styled.span`
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.sm}px;
`;

const Muted = styled.span`
  color: ${palette.slate};
  font-size: ${typography.size.xxs}px;
`;

const ErrorText = styled.span`
  display: block;
  color: ${palette.ink};
  font-size: ${typography.size.xs}px;
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

const Section = styled.div`
  display: grid;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
`;

/* One day: delivered and refused, side by side, numbers always beside the bar
   (design_system.md §2.4 — a state colour never stands alone). */
const DayRow = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr) 132px;
  align-items: center;
  gap: ${spacing.xs};
  font-size: ${typography.size.xs}px;

  ${down('sm')} {
    grid-template-columns: 72px minmax(0, 1fr);
    & > :last-child {
      grid-column: 1 / -1;
    }
  }
`;

const DayBar = styled.div`
  display: flex;
  height: 12px;
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  overflow: hidden;
  min-width: 0;
`;

const DaySeg = styled.span<{ $w: number; $c: string }>`
  display: block;
  height: 100%;
  width: ${p => Math.max(0, Math.min(100, p.$w))}%;
  /* A single day still needs a visible mark (memory: one-bucket charts). */
  min-width: ${p => (p.$w > 0 ? '3px' : '0')};
  background: ${p => p.$c};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

const Swatch = styled.span<{ $c: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: ${spacing.half};
  border-radius: 2px;
  background: ${p => p.$c};
  vertical-align: -1px;
`;

const Loading: React.FC = () => (
  <div aria-busy="true">
    {Array.from({ length: 5 }).map((_, i) => (
      /* eslint-disable-next-line react/no-array-index-key */
      <SkeletonRow key={i} />
    ))}
  </div>
);

const Panel: React.FC<{
  title: string;
  children: React.ReactNode;
  foot?: React.ReactNode;
}> = ({ title, children, foot }) => (
  <Card>
    <CardHead>
      <CardTitle>{title}</CardTitle>
    </CardHead>
    <CardBody>
      {children}
      {foot ? <div style={{ marginTop: spacing.sm }}>{foot}</div> : null}
    </CardBody>
  </Card>
);

const StatusTag: React.FC<{ status?: string | null }> = ({ status }) => {
  const s = statusOf(status);

  return <Tag tone={s.tone}>{s.word}</Tag>;
};

const edgeFor = (status?: string | null) => {
  const tone = statusOf(status).tone;
  if (tone === 'alert') return palette.alert;
  if (tone === 'warning') return palette.warning;
  if (tone === 'success') return palette.success;

  return palette.lineStrong;
};

/* The vendor's own words, with its code and status beside them. */
const VendorError: React.FC<{ e?: Row | null }> = ({ e }) => {
  if (!e) return <Muted>—</Muted>;
  const meta = [
    e.http_status ? `HTTP ${e.http_status}` : null,
    e.error_code || null,
  ].filter(Boolean);

  return (
    <>
      <ErrorText>{e.reason || '(no message)'}</ErrorText>
      {meta.length ? <Mono>{meta.join(' · ')}</Mono> : null}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   THE INDEX
   ═══════════════════════════════════════════════════════════════════════════ */

export const IntegrationsIndex: React.FC = () => {
  const nav = useNavigate();
  const [recheck, setRecheck] = useState(0);
  const { data, loading, error } = useAdminResource<Row>(
    recheck ? `/integrations?recheck=1&n=${recheck}` : '/integrations'
  );

  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading && !data) return <Loading />;
  if (!data) return null;

  const services = (data.services || []) as Row[];

  return (
    <>
      <Cards data-integrations-index>
        {services.map(s => {
          const st = statusOf(s.status);

          return (
            <ServiceCard
              key={s.key}
              type="button"
              $edge={edgeFor(s.status)}
              onClick={() => nav(`/admin/health/${s.key}`)}
              data-integration={s.key}
              data-integration-status={s.status}
              aria-label={`${s.label}: ${st.word}. Open its page.`}
            >
              <CardTop>
                <CardName>{s.label}</CardName>
                <StatusTag status={s.status} />
              </CardTop>
              <Muted>{s.vendor}</Muted>
              {s.reason ? <ErrorText>{s.reason}</ErrorText> : null}
              <Muted>
                Today: {num(s.today?.ok)} worked · {num(s.today?.failed)} failed
                {s.allowance
                  ? ` · ${num(s.allowance.used)} of ${num(s.allowance.limit)} allowed`
                  : ''}
              </Muted>
              <Muted>
                Last worked:{' '}
                {s.last_success ? when(s.last_success.at) : 'not since counting began'}
              </Muted>
              {s.last_failure ? (
                <Muted>
                  Last failed: {when(s.last_failure.at)} —{' '}
                  {s.last_failure.reason || '(no message)'}
                </Muted>
              ) : null}
            </ServiceCard>
          );
        })}
        {SYSTEM_CARDS.map(c => (
          <ServiceCard
            key={c.path}
            type="button"
            $edge={palette.lineStrong}
            onClick={() => nav(`/admin/${c.path}`)}
            aria-label={`${c.label}: open its screen.`}
          >
            <CardTop>
              <CardName>{c.label}</CardName>
              <Tag tone="neutral">Own screen</Tag>
            </CardTop>
            <Muted>{c.blurb}</Muted>
          </ServiceCard>
        ))}
      </Cards>
      <NoteBox>
        {data.counting_since
          ? `Real outcomes are counted since ${whenExact(data.counting_since)}. Anything older is only in System logs.`
          : 'No outcome has been counted yet. Older history is only in System logs.'}{' '}
        Days are in {data.tz}. Keys are never shown — only whether one is set and what the
        service answered.
      </NoteBox>
      <NoteBox data-alarm-devices={data.alarm_devices ?? ''}>
        {!data.alarm_push
          ? 'Phone alerts are off: push notifications are not set up on this server.'
          : Number(data.alarm_devices) > 0
            ? `When a service stops working, ${num(data.alarm_devices)} phone(s) or browser(s) of platform admins are told at once (once a day per problem).`
            : 'No platform admin has a phone or browser registered for notifications, so nobody is told outside this console when a service stops working. Turn on notifications in the app on your phone to be told.'}
      </NoteBox>
      <Toolbar>
        <Btn type="button" onClick={() => setRecheck(n => n + 1)}>
          Re-check every service now
        </Btn>
      </Toolbar>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ONE SERVICE
   ═══════════════════════════════════════════════════════════════════════════ */

const PerDay: React.FC<{ rows: Row[]; okWord: string; failWord: string }> = ({
  rows,
  okWord,
  failWord,
}) => {
  if (!rows.length) return <Empty>Nothing in this period.</Empty>;
  const max = Math.max(1, ...rows.map(r => Number(r.ok || 0) + Number(r.failed || 0)));

  return (
    <div style={{ display: 'grid', gap: spacing.xs }} data-integration-days>
      <Legend>
        <span>
          <Swatch $c={palette.teal} />
          {okWord}
        </span>
        <span>
          <Swatch $c={palette.alert} />
          {failWord}
        </span>
      </Legend>
      {rows.map(r => (
        <DayRow key={r.day}>
          <Mono>{r.day}</Mono>
          <DayBar aria-hidden="true">
            <DaySeg $w={(Number(r.ok || 0) / max) * 100} $c={palette.teal} />
            <DaySeg $w={(Number(r.failed || 0) / max) * 100} $c={palette.alert} />
          </DayBar>
          <span>
            {num(r.ok)} {okWord.toLowerCase()} · <strong>{num(r.failed)}</strong>{' '}
            {failWord.toLowerCase()}
          </span>
        </DayRow>
      ))}
    </div>
  );
};

const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);

    return true;
  } catch {
    return false;
  }
};

/* ── EMAIL: who did not get one ─────────────────────────────────────────── */

const MissedEmail: React.FC<{ missed: Row }> = ({ missed }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const invitations = (missed.invitations || []) as Row[];
  const notifications = (missed.notifications || []) as Row[];
  const reports = (missed.reports || []) as Row[];

  return (
    <Section data-email-missed>
      <Panel
        title={`Invitations that did not arrive (${invitations.length})`}
        foot={
          <NoteBox>
            The invitation itself is saved and its link works without email — only the
            email was lost. Lobbero keeps no copy of the link (only a fingerprint of it),
            so it cannot be shown here. The organization&apos;s owner or manager opens
            Invitations and presses Resend: the screen then shows the link and its QR code
            to pass on another way.
            {missed.invitations_before_counting
              ? ` ${missed.invitations_before_counting} other pending invitation(s) were sent before ` +
                'this console counted outcomes; whether they arrived is only in System logs.'
              : ''}
          </NoteBox>
        }
      >
        <AdminTable<Row>
          rows={invitations}
          rowKey={r => r.id}
          emptyLabel="Every pending invitation's last email went through."
          columns={[
            { key: 'who', header: 'Invited', render: r => r.name || '—' },
            {
              key: 'email',
              header: 'Address',
              render: r => (
                <span
                  style={{
                    display: 'inline-flex',
                    gap: spacing.xs,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ overflowWrap: 'anywhere' }}>{r.email}</span>
                  <Btn
                    type="button"
                    variant="ghost"
                    onClick={async () => {
                      if (await copyText(String(r.email || ''))) setCopied(r.id);
                    }}
                  >
                    {copied === r.id ? 'Copied' : 'Copy address'}
                  </Btn>
                </span>
              ),
            },
            { key: 'role', header: 'As', render: r => r.role },
            {
              key: 'org',
              header: 'Organization',
              render: r => r.org_name || r.organization_id,
            },
            {
              key: 'b',
              header: 'Building',
              wide: true,
              render: r => r.property_name || '—',
            },
            { key: 'at', header: 'Failed', nowrap: true, render: r => when(r.failed_at) },
            { key: 'why', header: 'SendGrid said', render: r => <VendorError e={r} /> },
            {
              key: 'exp',
              header: 'Link',
              render: r =>
                r.expired ? (
                  <Tag tone="warning">expired</Tag>
                ) : (
                  <Tag tone="success">valid</Tag>
                ),
            },
          ]}
        />
      </Panel>
      <Panel title={`Notifications whose email failed (${notifications.length})`}>
        <AdminTable<Row>
          rows={notifications}
          rowKey={r => r.id}
          emptyLabel="No notification email failed in this period."
          columns={[
            { key: 'at', header: 'When', nowrap: true, render: r => when(r.created_at) },
            {
              key: 'who',
              header: 'For',
              render: r =>
                `${r.recipient || '—'}${r.recipient_role ? ` (${r.recipient_role})` : ''}`,
            },
            { key: 'what', header: 'About', render: r => <Mono>{r.event_type}</Mono> },
            {
              key: 'org',
              header: 'Organization',
              wide: true,
              render: r => r.org_name || r.organization_id,
            },
            { key: 'why', header: 'SendGrid said', render: r => <VendorError e={r} /> },
          ]}
        />
      </Panel>
      <Panel title={`Daily reports that did not go (${reports.length})`}>
        <AdminTable<Row>
          rows={reports}
          rowKey={r => `${r.created_at}-${r.building_id}`}
          emptyLabel="No daily report failed in this period."
          columns={[
            { key: 'at', header: 'When', nowrap: true, render: r => when(r.created_at) },
            {
              key: 'b',
              header: 'Building',
              render: r => r.property_name || r.building_id || '—',
            },
            {
              key: 'org',
              header: 'Organization',
              wide: true,
              render: r => r.org_name || r.organization_id || '—',
            },
            { key: 'why', header: 'Said', render: r => <VendorError e={r} /> },
          ]}
        />
      </Panel>
    </Section>
  );
};

/* ── IDENTITY VERIFICATION (Stays plan S2): counts only ───────────────────
   Every cell below names its key one by one. A row is never spread, never
   walked with Object.keys/values and never stringified: a row that carries an
   unexpected key (a guest's name, a document number) must not reach the page. */

const AT_VENDOR: { key: string; what: string; field: string; tone: Tone }[] = [
  {
    key: 'extract',
    what: 'Waiting to be read back',
    field: 'extract_pending',
    tone: 'warning',
  },
  {
    key: 'redact',
    what: 'Redaction requested',
    field: 'redaction_pending',
    tone: 'warning',
  },
  {
    key: 'overdue',
    what: 'Redaction overdue (more than 5 days)',
    field: 'redaction_overdue',
    tone: 'alert',
  },
];

const countTone = (v: unknown, raised: Tone): Tone => {
  if (v === null || v === undefined || v === '') return 'neutral';

  return Number(v) === 0 ? 'success' : raised;
};

const IdentityPanels: React.FC<{ data: Row }> = ({ data }) => {
  const identity: Row = data.identity && typeof data.identity === 'object' ? data.identity : {};
  const byOrg: Row[] = Array.isArray(identity.by_org) ? identity.by_org : [];
  const atVendor: Row =
    identity.at_vendor && typeof identity.at_vendor === 'object' ? identity.at_vendor : {};
  const webhooks: Row[] = Array.isArray(data.webhooks) ? data.webhooks : [];
  // Independent Stays IS0: the in-house photo reads this month (counts only).
  const scans: Row =
    identity.id_scans_month && typeof identity.id_scans_month === 'object' ? identity.id_scans_month : {};
  const scanRows: { key: string; what: string; value: React.ReactNode }[] = [
    {
      key: 'mode',
      what: 'Photo reading on this server',
      value:
        scans.mode === 'ocr_and_manual' ? (
          <Tag tone="success">Photos are read</Tag>
        ) : (
          <Tag tone="warning">Guests type their details</Tag>
        ),
    },
    { key: 'scans', what: 'Photos sent to be read', value: num(scans.scans) },
    { key: 'ok', what: 'Read', value: num(scans.ok) },
    {
      key: 'failed',
      what: 'Not read',
      value: (
        <Tag tone={countTone(scans.failed, 'warning')}>
          {`${num(scans.failed)}${Number(scans.scans) ? ` (${String(scans.failure_rate ?? 0)}%)` : ''}`}
        </Tag>
      ),
    },
    {
      key: 'cost',
      what: 'AI cost (USD)',
      value: Number.isFinite(Number(scans.cost_micros)) ? (Number(scans.cost_micros) / 1e6).toFixed(4) : '—',
    },
  ];
  const vendorRows = AT_VENDOR.map(r => ({
    key: r.key,
    what: r.what,
    count: atVendor[r.field] as unknown,
    tone: r.tone,
  }));

  return (
    <Section data-identity-panels>
      <Panel title="ID photos read (this month)">
        <AdminTable<(typeof scanRows)[number]>
          rows={scanRows}
          rowKey={r => r.key}
          columns={[
            { key: 'w', header: 'What', render: r => r.what },
            { key: 'n', header: 'Value', numeric: true, render: r => r.value },
          ]}
        />
      </Panel>
      <Panel title="Verifications by organisation (this month)">
        <AdminTable<Row>
          rows={byOrg}
          rowKey={r => String(r.organization_id)}
          emptyLabel="No verification has started this month."
          columns={[
            {
              key: 'org',
              header: 'Organisation',
              render: r =>
                r.organization_name ? (
                  String(r.organization_name)
                ) : (
                  <Mono>{String(r.organization_id ?? '—')}</Mono>
                ),
            },
            { key: 's', header: 'Started', numeric: true, render: r => num(r.sessions) },
            {
              key: 'sub',
              header: 'Submitted',
              numeric: true,
              render: r => num(r.submitted),
            },
            { key: 'v', header: 'Verified', numeric: true, render: r => num(r.verified) },
            {
              key: 'ri',
              header: 'Needs another try',
              numeric: true,
              render: r => num(r.requires_input),
            },
            {
              key: 'c',
              header: 'Cancelled',
              numeric: true,
              render: r => num(r.canceled),
            },
          ]}
        />
      </Panel>
      <Panel title="Personal data still at the vendor">
        <AdminTable<(typeof vendorRows)[number]>
          rows={vendorRows}
          rowKey={r => r.key}
          columns={[
            { key: 'w', header: 'What', render: r => r.what },
            {
              key: 'n',
              header: 'Count',
              numeric: true,
              render: r => <Tag tone={countTone(r.count, r.tone)}>{num(r.count)}</Tag>,
            },
          ]}
        />
      </Panel>
      <Panel title="Webhook deliveries by event">
        <AdminTable<Row>
          rows={webhooks}
          rowKey={r => String(r.kind)}
          emptyLabel="No webhook delivery in this period."
          columns={[
            {
              key: 'k',
              header: 'Event',
              render: r => <Mono>{kindLabel(r.kind)}</Mono>,
            },
            { key: 'ok', header: 'Processed', numeric: true, render: r => num(r.ok) },
            { key: 'f', header: 'Failed', numeric: true, render: r => num(r.failed) },
            { key: 'at', header: 'Last', nowrap: true, render: r => when(r.last_at) },
          ]}
        />
      </Panel>
      <NoteBox data-identity-note>
        <div>
          Counts only. Guest names, document numbers and dates of birth are never shown
          here.
        </div>
        <div>
          If an alarm says personal data may be exposed, follow
          docs/stays/BREACH-NOTIFICATION-RUNBOOK.md: the 72-hour clock starts when we
          become aware.
        </div>
      </NoteBox>
    </Section>
  );
};

/* ── the service's own panels ───────────────────────────────────────────── */

const Extras: React.FC<{ data: Row }> = ({ data }) => {
  switch (data.key) {
    case 'email':
      return data.missed ? <MissedEmail missed={data.missed} /> : null;
    case 'address_search':
      return (
        <Panel
          title="Lookups per day against the allowance"
          foot={
            <NoteBox>
              The free plan allows about {num(data.allowance?.daily)} lookups a day (
              {num(data.allowance?.anonymous_daily)} of them for signed-out signup). A
              lookup is slow above {Math.round(Number(data.slow_after_ms || 4000) / 1000)}{' '}
              s and is given up after {Math.round(Number(data.timeout_ms || 8000) / 1000)}{' '}
              s.
            </NoteBox>
          }
        >
          <AdminTable<Row>
            rows={data.lookups_per_day || []}
            rowKey={r => r.day}
            emptyLabel="No lookup reached Geoapify in this period."
            columns={[
              { key: 'd', header: 'Day', render: r => <Mono>{r.day}</Mono> },
              { key: 'n', header: 'Lookups', numeric: true, render: r => num(r.lookups) },
              {
                key: 'a',
                header: 'Signed out',
                numeric: true,
                render: r => num(r.anonymous),
              },
              { key: 's', header: 'Slow', numeric: true, render: r => num(r.slow) },
              {
                key: 't',
                header: 'Timed out',
                numeric: true,
                render: r => num(r.timeouts),
              },
              {
                key: 'p',
                header: '95% within',
                numeric: true,
                render: r => (r.p95_ms ? `${num(r.p95_ms)} ms` : '—'),
              },
            ]}
          />
        </Panel>
      );
    case 'map_tiles':
      return (
        <Panel
          title="What browsers reported"
          foot={
            <NoteBox>
              The map&apos;s tiles are fetched by each person&apos;s browser. When a
              browser cannot load them it tells the server once per page; the health check
              asks the tile host from this server every ten minutes.
            </NoteBox>
          }
        >
          <AdminTable<Row>
            rows={data.browser_reports || []}
            rowKey={r => r.day}
            emptyLabel="No browser reported a map failure in this period."
            columns={[
              { key: 'd', header: 'Day', render: r => <Mono>{r.day}</Mono> },
              {
                key: 'b',
                header: 'Browser failures',
                numeric: true,
                render: r => num(r.browser_failures),
              },
              {
                key: 'p',
                header: 'Server check failures',
                numeric: true,
                render: r => num(r.probe_failures),
              },
            ]}
          />
        </Panel>
      );
    case 'stripe':
      return (
        <Section>
          <Panel title="The six-hourly billing check">
            <AdminTable<Row>
              rows={data.reconciles || []}
              rowKey={r => r.created_at}
              emptyLabel="The billing check has not run since counting began."
              columns={[
                {
                  key: 'at',
                  header: 'When',
                  nowrap: true,
                  render: r => when(r.created_at),
                },
                {
                  key: 'ok',
                  header: 'Result',
                  render: r =>
                    r.ok ? (
                      <Tag tone="success">all agree</Tag>
                    ) : (
                      <Tag tone="warning">needs a decision</Tag>
                    ),
                },
                {
                  key: 'why',
                  header: 'What it found',
                  render: r => (r.ok ? '—' : <VendorError e={r} />),
                },
              ]}
            />
          </Panel>
          <Panel title="Webhook deliveries by event">
            <AdminTable<Row>
              rows={data.webhooks || []}
              rowKey={r => r.kind}
              emptyLabel="No webhook delivery in this period."
              columns={[
                {
                  key: 'k',
                  header: 'Event',
                  render: r => <Mono>{kindLabel(r.kind)}</Mono>,
                },
                { key: 'ok', header: 'Processed', numeric: true, render: r => num(r.ok) },
                { key: 'f', header: 'Failed', numeric: true, render: r => num(r.failed) },
                { key: 'at', header: 'Last', nowrap: true, render: r => when(r.last_at) },
              ]}
            />
          </Panel>
        </Section>
      );
    case 'identity':
      return <IdentityPanels data={data} />;
    case 'gemini':
      return (
        <Panel
          title="Calls by feature (the cost ledger)"
          foot={
            <NoteBox>Tokens and cost per request are on the Lobbero IQ page.</NoteBox>
          }
        >
          <AdminTable<Row>
            rows={data.by_feature || []}
            rowKey={r => r.feature}
            emptyLabel="No call in this period."
            columns={[
              { key: 'f', header: 'Feature', render: r => <Mono>{r.feature}</Mono> },
              { key: 'c', header: 'Calls', numeric: true, render: r => num(r.calls) },
              { key: 'e', header: 'Errors', numeric: true, render: r => num(r.errors) },
              {
                key: 'q',
                header: 'Quota refusals',
                numeric: true,
                render: r => num(r.quota),
              },
              { key: 't', header: 'Tokens', numeric: true, render: r => num(r.tokens) },
            ]}
          />
        </Panel>
      );
    case 'web_push':
      return (
        <Panel
          title="By push service"
          foot={
            <NoteBox>
              {data.subscriptions
                ? `${num(data.subscriptions.live)} phone(s) or browser(s) registered; ${num(
                    data.subscriptions.retired
                  )} retired after their push service said they were gone.`
                : null}
            </NoteBox>
          }
        >
          <AdminTable<Row>
            rows={data.families || []}
            rowKey={r => r.family}
            emptyLabel="No push was sent in this period."
            columns={[
              { key: 'f', header: 'Service', render: r => kindLabel(r.family) },
              { key: 's', header: 'Delivered', numeric: true, render: r => num(r.sent) },
              { key: 'x', header: 'Failed', numeric: true, render: r => num(r.failed) },
              {
                key: 'e',
                header: 'Device gone',
                numeric: true,
                render: r => num(r.expired),
              },
            ]}
          />
        </Panel>
      );
    default:
      return null;
  }
};

/* The service's live state, as facts. */
const StateFacts: React.FC<{ data: Row }> = ({ data }) => {
  const st = data.state || {};
  const rows: [string, React.ReactNode][] = [
    ['Now', <StatusTag key="s" status={st.status} />],
  ];
  if (st.reason) rows.push(['Why', <ErrorText key="r">{st.reason}</ErrorText>]);
  if (st.explanation)
    rows.push(['What it means', <ErrorText key="e">{st.explanation}</ErrorText>]);
  if (st.probe) {
    rows.push([
      'Health check',
      <span key="p">
        {st.probe.skipped
          ? `skipped (${st.probe.skipped})`
          : st.probe.ok
            ? 'answered'
            : `refused — ${st.probe.reason || 'no reason given'}`}
        {st.probe.at ? <Muted> · {when(st.probe.at)}</Muted> : null}
      </span>,
    ]);
  }
  if (st.mode) rows.push(['Mode', String(st.mode).toUpperCase()]);
  if (st.usage)
    rows.push([
      'Today',
      `${num(st.usage.calls)} of about ${num(st.usage.limit)} lookups`,
    ]);
  if (st.breaker)
    rows.push([
      'Breaker',
      st.breaker === 'open' ? 'open — not asking for a while' : 'closed',
    ]);
  if (st.events) {
    rows.push([
      'Webhook events',
      `${num(st.events.total)} received · ${num(st.events.unprocessed)} waiting · last ${
        st.events.last_received_at ? when(st.events.last_received_at) : 'never'
      }`,
    ]);
  }
  if (st.tier) rows.push(['Tier', st.tier]);
  if (st.last_24h) {
    rows.push([
      'Last 24 hours',
      Object.entries(st.last_24h)
        .map(([k, v]) => `${num(v)} ${k}`)
        .join(' · '),
    ]);
  }
  rows.push([
    'Last worked',
    data.last_success ? when(data.last_success.at) : 'not since counting began',
  ]);
  rows.push([
    'Last failed',
    data.last_failure ? <VendorError key="f" e={{ ...data.last_failure }} /> : 'never',
  ]);

  return (
    <DefList>
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </React.Fragment>
      ))}
    </DefList>
  );
};

export const IntegrationScreen: React.FC = () => {
  const { key = '' } = useParams();
  const [days, setDays] = useState(30);
  const [recheck, setRecheck] = useState(0);
  const path = `/integrations/${encodeURIComponent(key)}?days=${days}${recheck ? `&recheck=1&n=${recheck}` : ''}`;
  const { data, loading, error, notFound } = useAdminResource<Row>(path);

  if (notFound) return <Empty>There is no service called “{key}”.</Empty>;
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading && !data) return <Loading />;
  if (!data) return null;

  const isEmail = data.key === 'email';

  return (
    <div data-integration-page={data.key}>
      <Toolbar>
        <Select
          aria-label="Period"
          value={days}
          onChange={e => setDays(Number(e.target.value))}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </Select>
        <Btn type="button" onClick={() => setRecheck(n => n + 1)}>
          Re-check now
        </Btn>
      </Toolbar>

      <TileGrid style={{ marginBottom: spacing.md }}>
        <Tile tone={statusOf(data.state?.status).tone === 'alert' ? 'alert' : undefined}>
          <TileLabel>{data.label}</TileLabel>
          <TileValue style={{ fontSize: 20 }}>
            {statusOf(data.state?.status).word}
          </TileValue>
          <TileFoot>{data.vendor}</TileFoot>
        </Tile>
        <Tile>
          <TileLabel>
            {isEmail ? 'Delivered' : 'Worked'} · {data.days} days
          </TileLabel>
          <TileValue>{num(data.totals?.ok)}</TileValue>
        </Tile>
        <Tile tone={Number(data.totals?.failed) > 0 ? 'alert' : undefined}>
          <TileLabel>
            {isEmail ? 'Refused' : 'Failed'} · {data.days} days
          </TileLabel>
          <TileValue>{num(data.totals?.failed)}</TileValue>
        </Tile>
        <Tile>
          <TileLabel>Counting since</TileLabel>
          <TileValue style={{ fontSize: 16 }}>
            {data.counting_since ? when(data.counting_since) : '—'}
          </TileValue>
          <TileFoot>Older: System logs</TileFoot>
        </Tile>
      </TileGrid>

      <Section>
        <Panel title="Now">
          <StateFacts data={data} />
        </Panel>

        <Panel title={`Per day (${data.tz})`}>
          <PerDay
            rows={data.per_day || []}
            okWord={isEmail ? 'Delivered' : 'Worked'}
            failWord={isEmail ? 'Refused' : 'Failed'}
          />
        </Panel>

        <Extras data={data} />

        <Panel title="By kind">
          <AdminTable<Row>
            rows={data.by_kind || []}
            rowKey={r => r.kind}
            emptyLabel="Nothing in this period."
            columns={[
              { key: 'k', header: 'Kind', render: r => kindLabel(r.kind) },
              {
                key: 'ok',
                header: isEmail ? 'Delivered' : 'Worked',
                numeric: true,
                render: r => num(r.ok),
              },
              {
                key: 'f',
                header: isEmail ? 'Refused' : 'Failed',
                numeric: true,
                render: r => num(r.failed),
              },
              {
                key: 'ls',
                header: 'Last worked',
                nowrap: true,
                wide: true,
                render: r => when(r.last_success_at),
              },
              {
                key: 'lf',
                header: 'Last failed',
                nowrap: true,
                render: r => when(r.last_failure_at),
              },
            ]}
          />
        </Panel>

        <Panel
          title={`What ${data.vendor} answered when it failed`}
          foot={
            <NoteBox>
              The service&apos;s own words. Email addresses are shortened to their first
              letter and domain, and anything that looks like a key is removed before it
              is stored.
            </NoteBox>
          }
        >
          <AdminTable<Row>
            rows={data.reasons || []}
            rowKey={r => `${r.reason}-${r.error_code}-${r.http_status}`}
            emptyLabel="Nothing failed in this period."
            columns={[
              { key: 'r', header: 'Error', render: r => <VendorError e={r} /> },
              { key: 'n', header: 'Times', numeric: true, render: r => num(r.n) },
              {
                key: 'k',
                header: 'On',
                wide: true,
                render: r => (r.kinds || []).map(kindLabel).join(', '),
              },
              {
                key: 'f',
                header: 'First',
                nowrap: true,
                wide: true,
                render: r => when(r.first_at),
              },
              { key: 'l', header: 'Last', nowrap: true, render: r => when(r.last_at) },
            ]}
          />
        </Panel>

        <Panel title="The last 50">
          <Scroller>
            <AdminTable<Row>
              rows={data.recent || []}
              rowKey={r => r.id}
              emptyLabel="No call has been counted yet."
              columns={[
                {
                  key: 'at',
                  header: 'When',
                  nowrap: true,
                  render: r => whenExact(r.created_at),
                },
                { key: 'k', header: 'Kind', render: r => kindLabel(r.kind) },
                {
                  key: 'ok',
                  header: 'Result',
                  render: r =>
                    r.ok ? (
                      <Tag tone="success">worked</Tag>
                    ) : (
                      <Tag tone="alert">failed</Tag>
                    ),
                },
                {
                  key: 'e',
                  header: 'Error',
                  render: r => (r.ok ? '—' : <VendorError e={r} />),
                },
                {
                  key: 'ms',
                  header: 'Took',
                  numeric: true,
                  wide: true,
                  render: r => (r.duration_ms != null ? `${num(r.duration_ms)} ms` : '—'),
                },
                {
                  key: 'o',
                  header: 'Organization',
                  wide: true,
                  render: r => r.org_name || '—',
                },
                {
                  key: 'ref',
                  header: 'For',
                  wide: true,
                  render: r =>
                    r.ref_type ? <Mono>{`${r.ref_type} ${r.ref_id}`}</Mono> : '—',
                },
              ]}
            />
          </Scroller>
        </Panel>
      </Section>
    </div>
  );
};
